// File: clientapp/src/components/BaseLayout.js
import React, { useState, useContext } from 'react';
import { Outlet, Link } from 'react-router-dom';
import {
    CssBaseline,
    Drawer,
    Box,
    Toolbar,
    useTheme,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import Header from './Header';
import Footer from './Footer';

import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat'; // import Chat icon
import SchemaIcon from '@mui/icons-material/Schema';

import { AuthContext } from '../contexts/AuthContext';

const drawerWidth = 225;

const BaseLayout = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const theme = useTheme();
    const { features } = useContext(AuthContext);

    // Toggle function for the left navbar
    const handleMenuToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    // Drawer content
    const drawer = (
        <Box sx={{ backgroundColor: 'rgb(51,51,51)', height: '100%' }}>
            <Toolbar sx={{ minHeight: '40px !important', height: '40px !important' }} />
            <List>
                <ListItem
                    button
                    component={Link}
                    to="/"
                    onClick={handleMenuToggle}
                    sx={{
                        height: 32,
                        pl: 2,
                        color: '#ffffff',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 0, mr: 1, color: '#ffffff' }}>
                        <HomeIcon sx={{ fontSize: 17 }} />
                    </ListItemIcon>
                    <ListItemText primary="Home" primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                </ListItem>

                <ListItem
                    button
                    component={Link}
                    to="/documents"
                    onClick={handleMenuToggle}
                    sx={{
                        height: 32,
                        pl: 2,
                        color: '#ffffff',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 0, mr: 1, color: '#ffffff' }}>
                        <DescriptionIcon sx={{ fontSize: 17 }} />
                    </ListItemIcon>
                    <ListItemText primary="Documents" primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                </ListItem>

                {features.includes("Manage My Workflow Tasks") && (
                    <ListItem
                        button
                        component={Link}
                        to="/tasks"
                        onClick={handleMenuToggle}
                        sx={{
                            height: 32,
                            pl: 2,
                            color: '#ffffff',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 0, mr: 1, color: '#ffffff' }}>
                            <AssignmentIcon sx={{ fontSize: 17 }} />
                        </ListItemIcon>
                        <ListItemText primary="Tasks" primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                    </ListItem>
                )}

                {
                    (features?.includes("Design Workflows")
                        || features?.includes("Audit Workflows")
                        || features?.includes("Manage rules"))
                    && (
                        <ListItem
                            button
                            component={Link}
                            to="/workflow"
                            onClick={handleMenuToggle}
                            sx={{
                                height: 32,
                                pl: 2,
                                color: '#ffffff',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, mr: 1, color: '#ffffff' }}>
                                <SchemaIcon sx={{ fontSize: 17 }} />
                            </ListItemIcon>
                            <ListItemText primary="Workflows" primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                        </ListItem>
                    )}
                {features?.includes("Use AI Assistant") && (
                    <ListItem
                        button
                        component={Link}
                        to="/assistant"
                        onClick={handleMenuToggle}
                        sx={{
                            height: 32,
                            pl: 2,
                            color: '#ffffff',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 0, mr: 1, color: '#ffffff' }}>
                            <ChatIcon sx={{ fontSize: 17 }} />
                        </ListItemIcon>
                        <ListItemText primary="Assistant" primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                    </ListItem>
                )}
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <CssBaseline />
            <Header onMenuClick={handleMenuToggle} />

            <Box sx={{ height: '40px' }} />

            {/* Main content area flex-grows to push footer down */}
            <Box component="main" sx={{ flexGrow: 1, p: 0 }}>
                <Outlet />
            </Box>

            <Footer />

            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={drawerOpen}
                onClose={handleMenuToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        backgroundColor: 'rgb(51,51,51)'
                    }
                }}
            >
                {drawer}
            </Drawer>

            {/* Permanent Drawer for larger screens (you can make this persistent or temporary) */}
            <Drawer
                variant="temporary"
                open={drawerOpen}
                onClose={handleMenuToggle}
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        backgroundColor: 'rgb(51,51,51)'
                    }
                }}
            >
                {drawer}
            </Drawer>
        </Box>
    );
};

export default BaseLayout;
